import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Upload, Spin, Collapse, Typography, Select } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import api from '../interceptors/api';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import StandardButton from "../components/StandardButton";
import ProtectedToolWrapper from '../components/ProtectedToolWrapper';

const { Dragger } = Upload;
const { Option } = Select;

const modelOptions = {
  Groq: ['whisper-large-v3-turbo', 'whisper-large-v3', 'distil-whisper-large-v3-en'],
  OpenAI: ['whisper-1']
};

const MultiModelTranscriptionTester = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({});
  const [error, setError] = useState(null);
  const [history, setHistory] = useState(() => {
    const saved = localStorage.getItem('multiModelTranscriptionHistory');
    return saved ? JSON.parse(saved) : [];
  });
  const [selectedProvider, setSelectedProvider] = useState('Groq');
  const [selectedModel, setSelectedModel] = useState(modelOptions['Groq'][0]);

  useEffect(() => {
    const shareId = searchParams.get("share");
    if (shareId) {
      loadSharedData(shareId);
    }
  }, []);

  const loadSharedData = (shareId) => {
    api.get(`/multi-model-transcribe/share/${shareId}`)
      .then(res => {
        const data = res.data;
        setSelectedProvider(data.provider);
        setSelectedModel(data.model);
        toast.success(t('shared_config_loaded'));
      })
      .catch(() => toast.error(t('server_error')));
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    setResults({});
    setError(null);

    const formData = new FormData();
    formData.append('audio', file);
    formData.append('provider', selectedProvider);
    formData.append('model', selectedModel);

    try {
      const res = await api.post('/multi-model-transcribe', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });

      setResults(res.data);

      const newEntry = {
        timestamp: new Date().toISOString(),
        fileName: file.name,
        provider: selectedProvider,
        model: selectedModel,
        results: res.data,
      };
      const updatedHistory = [newEntry, ...history];
      setHistory(updatedHistory);
      localStorage.setItem('multiModelTranscriptionHistory', JSON.stringify(updatedHistory));
    } catch (err) {
      setError(err.response?.data?.error || t('something_went_wrong'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProtectedToolWrapper showShareBanner={searchParams.has("share")}>
      <div className="max-w-5xl mx-auto">
        <Card>
          <Dragger
            accept="audio/*,video/mp4"
            maxCount={1}
            beforeUpload={(newFile) => {
              const isAudioOrMp4 =
                newFile.type.startsWith('audio/') || newFile.type === 'video/mp4';
              const isLt5M = newFile.size / 1024 / 1024 < 5;

              if (!isAudioOrMp4) {
                toast.error(t('upload_only_audio_mp4'));
                return Upload.LIST_IGNORE;
              }
              if (!isLt5M) {
                toast.error(t('file_size_limit'));
                return Upload.LIST_IGNORE;
              }

              setFile(newFile);
              return false;
            }}
            showUploadList={false}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p>{t('upload_audio_prompt')}</p>
            {file && (
              <div className="text-center text-sm text-gray-500 mt-2">
                {t('selected_file')}: <strong>{file.name}</strong>
                <StandardButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                  danger
                  className="ml-2"
                >
                  {t('remove_button')}
                </StandardButton>
              </div>
            )}
          </Dragger>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block font-medium mb-1">{t('provider')}</label>
              <Select
                className="w-full"
                value={selectedProvider}
                onChange={(provider) => {
                  setSelectedProvider(provider);
                  setSelectedModel(modelOptions[provider][0]);
                }}
              >
                {Object.keys(modelOptions).map((provider) => (
                  <Option key={provider} value={provider}>{provider}</Option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block font-medium mb-1">{t('model')}</label>
              <Select
                className="w-full"
                value={selectedModel}
                onChange={(val) => setSelectedModel(val)}
              >
                {modelOptions[selectedProvider].map((model) => (
                  <Option key={model} value={model}>{model}</Option>
                ))}
              </Select>
            </div>
          </div>

          <StandardButton
            onClick={handleUpload}
            className="mt-4"
            disabled={!file || loading}
            type="secondary" 
            loading={loading}
          >
            {t('transcribe_button')}
          </StandardButton>

          {error && <div className="text-red-500 mt-4">{error}</div>}

          {results && results.text && (
            <div className="mt-6 space-y-4">
              <Card title={t('output')}>
                <pre className="whitespace-pre-wrap text-sm">{results.text}</pre>
                <StandardButton
                  size="small"
                  onClick={() => navigator.clipboard.writeText(results.text)}
                  type="secondary"
                  className="mt-2"
                >
                  {t('copy')}
                </StandardButton>
              </Card>

              {results.segments?.length > 0 && (
                <Card title={t('timestamps')}>
                  <ul className="text-sm space-y-1 max-h-64 overflow-auto">
                    {results.segments.map((seg, index) => (
                      <li key={index}>
                        <strong>[{seg.start.toFixed(2)}s - {seg.end.toFixed(2)}s]</strong>: {seg.text}
                      </li>
                    ))}
                  </ul>
                </Card>
              )}
            </div>
          )}

          {history.length > 0 && (
            <div className="mt-10">
              <h3 className="text-lg font-semibold mb-4">{t('history')}</h3>
              <Collapse accordion>
                {history.map((h, i) => (
                  <Collapse.Panel
                    key={i}
                    header={`${h.fileName} | ${h.provider} - ${h.model} | ${new Date(h.timestamp).toLocaleString()}`}
                  >
                    <div className="mb-4">
                      <Typography.Text strong>{t('output')}</Typography.Text>
                      <p className="text-sm whitespace-pre-wrap">{h.results.text}</p>

                      {h.results.segments?.length > 0 && (
                        <>
                          <Typography.Text strong className="mt-2 block">{t('timestamps')}</Typography.Text>
                          <ul className="text-sm space-y-1 max-h-64 overflow-auto mt-1">
                            {h.results.segments.map((seg, index) => (
                              <li key={index}>
                                <strong>[{seg.start.toFixed(2)}s - {seg.end.toFixed(2)}s]</strong>: {seg.text}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                  </Collapse.Panel>
                ))}
              </Collapse>
              <StandardButton onClick={() => {
                localStorage.removeItem('multiModelTranscriptionHistory');
                setHistory([]);
              }} danger type="secondary" className="mt-4">
                {t('clear_history_button')}
              </StandardButton>
            </div>
          )}
        </Card>
      </div>
    </ProtectedToolWrapper>
  );
};

export default MultiModelTranscriptionTester;